import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    urlQueryParamFlags,
    featureFlags,
    FEATURE_FLAG_URL_PREFIX,
    isUrlQueryParamFlag,
    isFxUrlOverrideEnabled,
    defaultFlags,
} from './featureFlags';
import { forEach, toLower } from 'lodash';
import { CILogger } from 'common/log/log.provider';
import styled from 'styled-components';
import { Loading } from 'common/utils/loading';
import { useFxIntegrationInitialization } from './hooks/useFxIntegrationInitialization';

export const FEATURE_FLAGS_STORAGE_KEY = 'ciFeatureFlags';

export function FeatureFlagsSynchronizer({ children }) {
    const history = useHistory();
    const isUpdatingURLRef = useRef(false);
    const fxIntegrationState = useFxIntegrationInitialization();

    useEffect(function loadStoredFeatureFlags() {
        try {
            const storedFlags = sessionStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
            if (storedFlags) {
                const parsedFlags = JSON.parse(storedFlags);

                forEach(urlQueryParamFlags, (flag) => {
                    if (parsedFlags[flag] !== undefined) {
                        if (flag === 'fxIntegration' && !isFxUrlOverrideEnabled()) {
                            return;
                        }

                        featureFlags[flag] = parsedFlags[flag];
                    }
                });
            }
        } catch (e) {
            CILogger.getInstance().error(`Failed to load feature flags ${e}`);
        }
    }, []);

    useEffect(
        function setupFeatureFlagsHistoryListener() {
            if (!fxIntegrationState.isInitialized) {
                return;
            }

            const synchronizeUrlParamsWithFeatureFlags = (location) => {
                if (isUpdatingURLRef.current) {
                    isUpdatingURLRef.current = false;

                    return;
                }

                const currentParams = new URLSearchParams(location.search);
                const flagsToStore = {};
                const newParams = new URLSearchParams();

                // Copy other parameters
                forEach(Array.from(currentParams.entries()), ([key, value]) => {
                    if (!isUrlQueryParamFlag(key)) {
                        newParams.append(key, value);
                    }
                });

                forEach(urlQueryParamFlags, (flag) => {
                    if (flag === 'fxIntegration' && !isFxUrlOverrideEnabled()) {
                        return;
                    }

                    const flagParamKey = `${FEATURE_FLAG_URL_PREFIX}${flag}`;
                    const urlValue = currentParams.get(flagParamKey) || currentParams.get(toLower(flagParamKey));
                    if (urlValue !== null) {
                        const flagValue = urlValue === 'true';
                        featureFlags[flag] = flagValue;
                        flagsToStore[flag] = flagValue;
                    }

                    // Add to new params only if not default
                    if (featureFlags[flag] !== defaultFlags[flag]) {
                        newParams.append(flagParamKey, String(featureFlags[flag]));
                        flagsToStore[flag] = featureFlags[flag];
                    }
                });

                // Store feature flags
                try {
                    sessionStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flagsToStore));
                } catch (e) {
                    CILogger.getInstance().error(`Failed to store feature flags ${e}`);
                }

                const newParamsString = newParams.toString();
                const currentParamsString = currentParams.toString();

                // Update URL
                if (newParamsString !== currentParamsString) {
                    isUpdatingURLRef.current = true;
                    history.replace({
                        pathname: location.pathname,
                        search: newParamsString,
                        hash: location.hash,
                        state: location.state,
                    });
                }
            };

            const unlisten = history.listen(synchronizeUrlParamsWithFeatureFlags);

            // Run once on mount
            synchronizeUrlParamsWithFeatureFlags(history.location);

            return unlisten;
        },
        [history, fxIntegrationState.isInitialized]
    );

    return fxIntegrationState.isInitialized ? (
        <>{children}</>
    ) : (
        <LoadingContainer>
            <Loading isLoading={true} />
        </LoadingContainer>
    );
}

const LoadingContainer = styled('div')(() => ({
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
}));
